/* eslint-disable react-hooks/exhaustive-deps */
import classNames from "classnames";
import { useContext, useEffect, useMemo, useState } from "react";
import { Container, Tab, Tabs } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { AppRulesContext } from "../../App";
import ITRFilingApi from "../../api/ITRFilingApi";
import PlansApi from "../../api/PlansApi";
import PageFilterWrap from "../../components/PageFilterWrap";
import PageHeader from "../../components/PageHeader";
import CardLoader from "../../components/content-loader/CardLoader";
import TableLoader from "../../components/content-loader/TableLoader";
import ITRFilingsForUser from "../../components/itr-filing/ITRFilingsForUser";
import { OverlaySpinner } from "../../components/placeholders";
import NothingAvailable from "../../components/placeholders/NothingAvailable";
import { ITRFilingsTable } from "../../components/tables";
import { AppAuthRoles } from "../../config/app-constants";
import { EfilingCSVColsName } from "../../config/filingConstants";
import {
  EToastvariant,
  IEfilingDetailsData,
  IFilingFilters,
  IFilingResponse,
  IPlansRoot,
  ITRFilingParams
} from "../../config/interfaces";
import { useAppDispatch, useAppSelector } from "../../redux/Store";
import { filingFiltersSelector, setFilingFilters } from "../../redux/slice/FilterSlice";
import {
  ITRFilingSelector,
  setCheckOutPlan,
  setCurrentFilingUUID
} from "../../redux/slice/ITRFilingSlice";
import { loggedInUserSelector } from "../../redux/slice/LoggedInUserSlice";
import { showToastMsg } from "../../redux/slice/ToastMsgSlice";
import { StorageService, UtilService } from "../../services";
import { FilingFormInitValues } from "./efiling.config";

let INIT_FILTER_VALUES: IFilingFilters = {
  q: "",
  orderBy: "createdAt",
  order: "DESC",
  rowCount: 10
};

const ITRFilingList = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { rulesSvc } = useContext(AppRulesContext);
  const { plansData } = useAppSelector(ITRFilingSelector);
  const { loggedInUser } = useAppSelector(loggedInUserSelector);

  const filingFilters = useAppSelector(filingFiltersSelector);
  const [filings, setFilings] = useState<IFilingResponse[]>([]);
  const [userFilingData, setUserFilingData] = useState<IEfilingDetailsData[]>([]);
  const [activeTab, setActiveTab] = useState<"self" | "assisted">("assisted");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isPageLoaded, setIsPageLoaded] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>(
    filingFilters?.q || INIT_FILTER_VALUES?.q || ""
  );
  /* TODO it would be removed if we are not going to show filters in url as url params and use filingFiltersSelector */
  const [filterValues, setFilterValues] = useState<IFilingFilters>(() => {
    // on load set filter values
    INIT_FILTER_VALUES = {
      ...INIT_FILTER_VALUES
    };
    // set filter values if exists (navigate back), otherwise init values
    return filingFilters || INIT_FILTER_VALUES;
  });
  const [isFiltered, setIsFiltered] = useState<boolean>(false);

  // page index will start from 0 for pagination, and will be inc by 1 in api call
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);
  const [currentTimeStamp, setCurrentTimeStamp] = useState<number>(new Date().getTime());
  const [pageCount, setPageCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [isCreating, setIsCreating] = useState<boolean>(false);

  // store plan id in local storage to check marketPlans or not

  const handleCreateEmptyRecord = async () => {
    try {
      const _planId = await StorageService.getMarketPlansUrl();
      if (!_planId) {
        return;
      }

      const filingData = {
        efilingData: FilingFormInitValues,
        name: "",
        pan: "",
        assessmentYear: "2024-25"
      };

      const filingApi = ITRFilingApi.getInstance();
      const { uuid } = await filingApi.createITRFiling(filingData);

      let plans: IPlansRoot[] = [];
      if (!plansData?.length) {
        const api = PlansApi.getInstance();
        const { records } = await api.getPlans({});
        plans = records?.filter((efPlan) => efPlan.uuid === _planId) || [];
      } else {
        plans = plansData?.filter((efPlan) => efPlan.uuid === _planId) || [];
      }

      dispatch(setCheckOutPlan(plans[0]));
      navigate(`/itr-filings/${uuid}/assisted/plan-checkout`, { replace: true });
    } catch (error: any) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      // remove planUUID, that is used for purchase plan from external link
      await StorageService.removeMarketPlansUrl();
    }
  };

  useEffect(() => {
    getAllFilings();
  }, [currentTimeStamp, activeTab]);

  const getAllFilings = async () => {
    try {
      setIsLoading(currentPageIndex === 0 && !isPageLoaded);
      setIsFetching(true);

      const _params: ITRFilingParams = {
        search: filterValues.q,
        offset: offset,
        count: filterValues.rowCount,
        orderBy: filterValues.orderBy,
        order: filterValues.order,
        authorized: undefined
      };
      if (loggedInUser?.role === AppAuthRoles.CA) {
        _params.authorized = activeTab === "assisted" ? true : false;
      }
      const filingsApi = ITRFilingApi.getInstance();
      const { count, records = [] } = await filingsApi.getITRFilings(_params);

      // it will check it is new user or existing user
      const _planId = await StorageService.getMarketPlansUrl();
      if (_planId) {
        handleCreateEmptyRecord();
      } else {
        await StorageService.removeMarketPlansUrl();
        const _data = records || [];
        setFilings(() => _data);
        setPageCount(Math.ceil(Number(count / 10)));
        setIsFiltered(showClearFilter);

        let _filings: any = {};
        for (let index = 0; index < _data.length; index++) {
          const record = _data[index];
          if (_filings[record.pan]) {
            _filings[record.pan]?.EfilingData?.push(record);
          } else {
            const _newData = {
              name: record.name,
              pan: record.pan,
              fileUsedBy: record.fileUsedBy,
              EfilingData: [record]
            };
            _filings[record.pan] = _newData;
          }
        }
        setUserFilingData(() => Object.values(_filings));
      }
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setIsPageLoaded(true);
      setIsLoading(false);
      setIsFetching(false);
    }
  };

  const handleOnPageChange = (event: any) => {
    if (event.selected > 0) {
      setOffset(event.selected * 10);
    } else {
      setOffset(0);
    }
    setCurrentPageIndex(Math.ceil(event.selected));
    setCurrentTimeStamp(new Date().getTime());
  };

  const handleSearchQuery = () => {
    const _values = {
      ...filterValues,
      q: searchQuery
    };
    setFilterValues(() => _values);
    dispatch(setFilingFilters(_values));
    setCurrentPageIndex(0);
    setCurrentTimeStamp(new Date().getTime());
  };

  const showClearFilter = useMemo(() => {
    if (searchQuery || filterValues?.q) {
      return true;
    }
    return false;
  }, [filterValues, searchQuery]);

  const handleCreateEmptyFiling = async () => {
    try {
      setIsCreating(() => true);
      const filingData = {
        efilingData: FilingFormInitValues,
        name: "",
        pan: "",
        assessmentYear: "2024-25"
      };

      const filingApi = ITRFilingApi.getInstance();
      const { uuid } = await filingApi.createITRFiling(filingData);

      dispatch(setCurrentFilingUUID(uuid));
      navigate(`/itr-filings/${uuid}`);
    } catch (error: any) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setIsCreating(() => false);
    }
  };

  const onExportButtonClick = async () => {
    try {
      setIsExporting(() => true);
      const _params: ITRFilingParams = {
        count: -1,
        authorized: undefined
      };

      const filingsApi = ITRFilingApi.getInstance();
      const { records } = await filingsApi.getITRFilings(_params);
      const fileName = `mytaxclub-efilings-${UtilService.convertToStandardDateFormat(new Date())}`;
      const flattenedData = EfilingCSVColsName(records);
      UtilService.downloadCSV(fileName, flattenedData);
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setIsExporting(() => false);
    }
  };

  return (
    <>
      <Container className={classNames(loggedInUser?.role !== AppAuthRoles.ADMIN && "w-1170")}>
        {isExporting ? <OverlaySpinner /> : null}
        {isLoading ? (
          <>
            <PageHeader title="Filings" />
            <CardLoader cardType="list" />
          </>
        ) : (
          <>
            {!filings?.length && !isFiltered && loggedInUser?.role === AppAuthRoles.ADMIN ? (
              <>
                <PageHeader title="Filings" />
                <div className="text-center my-5">
                  <NothingAvailable message="Currently no filing is available." />
                </div>
              </>
            ) : (
              <>
                <PageHeader
                  title="Filings"
                  primaryBtnTitle="New Filing"
                  onPrimaryBtnPress={() => handleCreateEmptyFiling()}
                  isDisable={isCreating}
                  primaryBtnRules={rulesSvc.createEFiling}
                  onExportBtnPress={() => onExportButtonClick()}
                  filtersForm={
                    <>
                      <PageFilterWrap
                        filtersForm={<></>}
                        showSearchFilter={true}
                        searchPlaceholder="Search by Name or PAN"
                        searchValue={searchQuery}
                        searchOnChange={(_query) => {
                          setSearchQuery(_query);
                        }}
                        onClickSearchBtn={handleSearchQuery}
                        showAdvFilters={false}
                      />
                    </>
                  }
                />

                {/* only visible to admin */}
                {isFetching && loggedInUser?.role === AppAuthRoles.ADMIN ? (
                  <TableLoader portalModule="filing" />
                ) : loggedInUser?.role === AppAuthRoles.ADMIN ? (
                  <ITRFilingsTable
                    filingsData={filings}
                    pageCount={pageCount}
                    forcePage={currentPageIndex}
                    handleOnPageChange={handleOnPageChange}
                    noRecords={isFiltered && !filings?.length}
                    handleRefresh={() => setCurrentTimeStamp(new Date().getTime())}
                  />
                ) : null}

                {/* only visible to ca */}
                {loggedInUser?.role === AppAuthRoles.CA ? (
                  <Tabs
                    activeKey={activeTab}
                    id="efiling-tabs"
                    className="border-nav-tabs compact-tabs mt-5"
                    onSelect={(activeKey) => {
                      switch (activeKey) {
                        case "assisted":
                          setActiveTab("assisted");
                          break;
                        case "self":
                          setActiveTab("self");
                          break;
                      }
                    }}
                  >
                    <Tab eventKey="assisted" title="Assisted">
                      {isFetching ? (
                        <CardLoader cardType="list" />
                      ) : (
                        <>
                          {!filings?.length && !isFiltered ? (
                            <div className="text-center my-5">
                              <NothingAvailable message="Currently no filing is available." />
                            </div>
                          ) : (
                            <ITRFilingsForUser
                              filingsData={userFilingData}
                              noRecords={isFiltered && !userFilingData.length}
                              handleRefresh={() => setCurrentTimeStamp(new Date().getTime())}
                              pageCount={pageCount}
                              forcePage={currentPageIndex}
                              handleOnPageChange={handleOnPageChange}
                            />
                          )}
                        </>
                      )}
                    </Tab>
                    <Tab eventKey="self" title="Self">
                      {isFetching ? (
                        <CardLoader cardType="list" />
                      ) : (
                        <>
                          {!filings?.length && !isFiltered ? (
                            <div className="text-center my-5">
                              <NothingAvailable message="Currently no filing is available." />
                            </div>
                          ) : (
                            <ITRFilingsForUser
                              filingsData={userFilingData}
                              noRecords={isFiltered && !userFilingData.length}
                              handleRefresh={() => setCurrentTimeStamp(new Date().getTime())}
                              pageCount={pageCount}
                              forcePage={currentPageIndex}
                              handleOnPageChange={handleOnPageChange}
                            />
                          )}
                        </>
                      )}
                    </Tab>
                  </Tabs>
                ) : null}

                {/* only visible to user */}
                {isFetching && loggedInUser?.role === AppAuthRoles.USER ? (
                  <CardLoader cardType="list" />
                ) : loggedInUser?.role === AppAuthRoles.USER ? (
                  <>
                    {!filings?.length && !isFiltered ? (
                      <div className="text-center my-5">
                        <NothingAvailable message="Currently no filing is available." />
                      </div>
                    ) : (
                      <ITRFilingsForUser
                        filingsData={userFilingData}
                        noRecords={isFiltered && !userFilingData.length}
                        handleRefresh={() => setCurrentTimeStamp(new Date().getTime())}
                        pageCount={pageCount}
                        forcePage={currentPageIndex}
                        handleOnPageChange={handleOnPageChange}
                      />
                    )}
                  </>
                ) : null}
              </>
            )}
          </>
        )}
      </Container>
    </>
  );
};

export default ITRFilingList;
